// ── Gap-Based Travel Segments ──
// Pure travel util for a derived DayView: one segment (a calendar "travel
// pin") per pair of consecutive timed blocks that both have coordinates.
// Informational only — never blocks a mutation.

import type {
  DayBlock,
  DayView,
  POI,
  TravelMode,
  ZonedTimeString,
} from "@poi-planner/shared";
import {
  DEFAULT_TRAVEL_MODE_PREFERENCES,
  autoTravelMode,
  estimateTravel,
  haversineDistanceKm,
  type Coords,
  type TravelEstimate,
  type TravelModePreferences,
} from "./geo";
import { blockEndTime, blockStartTime } from "./dayViews";
import { minutesBetween } from "./zonedTime";
import { hasUsableCoordinates } from "./dayMapPois";

export interface TravelEndpoint {
  name: string;
  coords: Coords;
}

export interface TravelSegment {
  /** Stable identity for this pin: `${fromBlockId}>${toBlockId}` */
  segmentKey: string;
  fromBlockId: string;
  toBlockId: string;
  /** Arrival item's schedule id — the key into `Schedule.travelModes` */
  targetId: string;
  from: TravelEndpoint;
  to: TravelEndpoint;
  mode: TravelMode;
  /** "override" when the user picked the mode for this pin */
  modeSource: "auto" | "override";
  /** What the trip's preferences would pick for this hop */
  autoMode: TravelMode;
  /** Offline straight-line estimate for `mode` (fallback until resolved) */
  estimate: TravelEstimate;
  /** When the traveller leaves: the previous block's end */
  departureTime: ZonedTimeString;
  /** Actual gap between the blocks (next.start − prev.end), in minutes */
  gapMinutes: number;
  /** True when the estimated travel time exceeds the actual gap */
  overrun: boolean;
}

function poiCoords(poi: POI | undefined): Coords | null {
  if (!poi || !hasUsableCoordinates(poi)) return null;
  return { lat: poi.lat, lng: poi.lng };
}

function blockScheduleId(block: DayBlock): string {
  return block.type === "poi" ? block.scheduledPOI.id : block.note.id;
}

/**
 * Where a block is, seen from one side of a trip between blocks. A POI block
 * is at its POI. A note (e.g. a train) is left from its end POI and arrived
 * at via its start POI — walking to a train goes to the departure station —
 * each falling back to the other. A note with neither is coordinate-less and
 * suppresses the segment: a misleading pin is worse than none.
 */
function blockEndpoint(
  block: DayBlock,
  side: "leaving" | "arriving",
  poiMap: Map<string, POI>,
): TravelEndpoint | null {
  if (block.type === "poi") {
    const poi = poiMap.get(block.scheduledPOI.poiId);
    const coords = poiCoords(poi);
    return poi && coords ? { name: poi.name, coords } : null;
  }

  const startPoi = block.note.startPoiId
    ? poiMap.get(block.note.startPoiId)
    : undefined;
  const endPoi = block.note.endPoiId
    ? poiMap.get(block.note.endPoiId)
    : undefined;
  const [preferred, fallback] =
    side === "leaving" ? [endPoi, startPoi] : [startPoi, endPoi];
  const poi = poiCoords(preferred) ? preferred : fallback;
  const coords = poiCoords(poi);
  return poi && coords ? { name: poi.name, coords } : null;
}

/**
 * Computes travel segments between consecutive blocks of a day. A segment is
 * emitted only when both neighbouring blocks have coordinates and the
 * distance is travellable over ground (estimateTravel returns null beyond).
 * `travelModes` holds per-arrival-item overrides; otherwise the mode is
 * picked from the straight-line distance and the trip's preferences.
 */
export function computeTravelSegments(
  dayView: DayView,
  pois: POI[],
  travelModes: Record<string, TravelMode> = {},
  preferences: TravelModePreferences = DEFAULT_TRAVEL_MODE_PREFERENCES,
): TravelSegment[] {
  const poiMap = new Map(pois.map((p) => [p.id, p]));
  const segments: TravelSegment[] = [];

  // DayView blocks are already sorted by start instant
  for (let i = 0; i < dayView.blocks.length - 1; i++) {
    const prev = dayView.blocks[i];
    const next = dayView.blocks[i + 1];

    const from = blockEndpoint(prev, "leaving", poiMap);
    const to = blockEndpoint(next, "arriving", poiMap);
    if (!from || !to) continue;

    const targetId = blockScheduleId(next);
    const override = travelModes[targetId];
    const autoMode = autoTravelMode(
      haversineDistanceKm(from.coords, to.coords),
      preferences,
    );
    const mode = override ?? autoMode;
    const estimate = estimateTravel(from.coords, to.coords, mode);
    if (!estimate) continue;

    const departureTime = blockEndTime(prev);
    const gapMinutes = minutesBetween(departureTime, blockStartTime(next));
    // Overlapping blocks have no travel gap to annotate — a pin on top of
    // the cascade is noise, not information
    if (gapMinutes < 0) continue;
    segments.push({
      segmentKey: `${prev.id}>${next.id}`,
      fromBlockId: prev.id,
      toBlockId: next.id,
      targetId,
      from,
      to,
      mode,
      modeSource: override ? "override" : "auto",
      autoMode,
      estimate,
      departureTime,
      gapMinutes,
      overrun: estimate.travelMinutes > gapMinutes,
    });
  }

  return segments;
}
